<template>
    <div class="table-records">
        <div
            v-for="record in records"
            :key="record._id"
            class="table-row"
            :id="`record-${record._id}`"
            :class="{ selected: isSelectionModeActive && isSelected(record._id) }"
            :data-record-id="record._id"
            @mouseover="!isSelectionModeActive && showCheckbox(record._id)"
            @mouseleave="!isSelectionModeActive && hideCheckbox(record._id)"
        >
            <div class="table-cell record-id-cell">
                <span v-if="!isSelectionModeActive && !isCheckboxVisible(record._id)">{{ record.recordId }}</span>
                <a-checkbox
                    v-else
                    :checked="isSelected(record._id)"
                    @change="$emit('toggle-selection', record._id)"
                ></a-checkbox>
            </div>

            <div
                class="table-cell"
                v-for="(field, index) in visibleFields"
                :key="field._id"
                @click="setActiveCell(record._id, field._id)"
                :class="{ active: isActiveCell(record._id, field._id) }"
                @keydown.tab.exact.prevent="moveToNextCell(record._id, index)"
                @keydown.enter.exact.prevent="editCell(record._id, field._id)"
                tabindex="0"
                :data-field-type="field.fieldType"
            >
                <StringCell
                    v-if="['text', 'long-text', 'email', 'url', 'phone'].includes(field.fieldType)"
                    :record="record"
                    :field="field"
                    @update-record="updateRecord($event)"
                    ref="stringCells"
                />
                <CheckboxCell
                    v-else-if="field.fieldType === 'checkbox'"
                    :record="record"
                    :field="field"
                    @update-record="updateRecord($event)"
                />
                <NumberCell
                    v-else-if="field.fieldType === 'number'"
                    :record="record"
                    :field="field"
                    @update-record="updateRecord($event)"
                />
                <CurrencyCell
                    v-else-if="field.fieldType === 'currency'"
                    :record="record"
                    :field="field"
                    @update-record="updateRecord($event)"
                />
                <DropdownCell
                    v-else-if="field.fieldType === 'dropdown'"
                    :record="record"
                    :field="field"
                    @update-record="updateRecord($event)"
                />
                <DateCell
                    v-else-if="field.fieldType === 'datetime'"
                    :record="record"
                    :field="field"
                    @update-record="updateRecord($event)"
                />
                <!-- automation-button tipi için ButtonCell bileşeni eklendi -->
                <ButtonCell
                    v-else-if="field.fieldType === 'automation-button'"
                    :record="record"
                    :field="field"
                    :tableId="table._id"
                    @update-record="updateRecord($event)"
                />

                <div v-else class="table-cell-inner">
                    {{ getFieldValue(record, field._id) }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import StringCell from "./cells/StringCell.vue";
import CheckboxCell from "./cells/CheckboxCell.vue";
import NumberCell from "./cells/NumberCell.vue";
import CurrencyCell from "./cells/CurrencyCell.vue";
import { TABLE_API } from "../../../../api/table";
import DropdownCell from "./cells/DropdownCell.vue";
import DateCell from "./cells/DateCell.vue";
import ButtonCell from "./cells/ButtonCell.vue"; // ButtonCell import edildi

export default Vue.extend({
    name: "TableRecords",
    props: {
        table: {
            type: Object,
            required: true,
        },
        records: {
            type: Array,
            required: true,
        },
        isSelectionModeActive: Boolean,
        selectedRecords: Array,
        visibleFields: {
            type: Array,
            required: true,
        },
    },
    components: {
        StringCell,
        CheckboxCell,
        NumberCell,
        CurrencyCell,
        DropdownCell,
        DateCell,
        ButtonCell, // ButtonCell bileşeni eklendi
    },
    data() {
        return {
            activeCell: null,
            checkboxVisibility: {},
        };
    },
    methods: {
        getFieldValue(record, fieldId) {
            return record.data[fieldId] || "";
        },
        setActiveCell(recordId, fieldId) {
            this.activeCell = `${recordId}-${fieldId}`;
        },
        isActiveCell(recordId, fieldId) {
            return this.activeCell === `${recordId}-${fieldId}`;
        },
        async updateRecord({ recordId, fieldId, value }) {
            const updatingFields = {
                [fieldId]: value,
            };
            console.log("updatingFields", {
                recordId,
                fieldId,
                value,
                updatingFields,
            });
            TABLE_API.updateRecord(this.table._id, recordId, updatingFields)
                .then((record) => {
                    this.$emit("record-updated", record);
                    this.$message.success(this.$t("tables.recordUpdatedSucs"));
                })
                .catch(() => {
                    this.$message.error(this.$t("tables.failedToUpdateRecord"));
                });
        },
        moveToNextCell(recordId, currentIndex) {
            const nextIndex = (currentIndex + 1) % this.table.fields.length;
            const nextField = this.table.fields[nextIndex];

            if (nextIndex === 0) {
                const currentRecordIndex = this.records.findIndex((record) => record._id === recordId);
                const nextRecord = this.records[currentRecordIndex + 1];
                if (nextRecord) {
                    this.setActiveCell(nextRecord._id, nextField._id);
                }
            } else {
                this.setActiveCell(recordId, nextField._id);
            }
        },
        editCell(recordId, fieldId) {
            const cellKey = `${recordId}-${fieldId}`;
            const stringCell = this.$refs.stringCells.find((cell) => cell.cellKey === cellKey);
            console.log("editCell stringCells", this.$refs.stringCells);
            if (stringCell && !stringCell.editing) {
                stringCell.startEditing();
            }
        },
        showCheckbox(recordId) {
            this.$set(this.checkboxVisibility, recordId, true);
        },
        hideCheckbox(recordId) {
            this.$set(this.checkboxVisibility, recordId, false);
        },
        isCheckboxVisible(recordId) {
            return this.isSelectionModeActive || this.checkboxVisibility[recordId];
        },
        isSelected(recordId) {
            return this.selectedRecords.includes(recordId);
        },
    },
});
</script>

<style scoped lang="scss">
.table-records {
    width: max-content;

    .table-row {
        display: flex;
        border-bottom: 1px solid #ccc;

        .table-cell {
            min-width: 245px;
            max-width: 245px;
            padding: 2px 15px;
            height: 40px;
            display: flex;
            align-items: center;

            border-right: 1px solid #ccc;
            position: relative;

            &:last-child {
                //border-right: none;
            }

            &.active {
                border: 2px solid #007bff;
            }

            &.selected {
                background-color: #e6f7ff;
            }

            &[data-field-type="automation-button"] {
                width: 100%;
            }
        }

        &:hover {
            background-color: #f5f5f5;
        }

        &.selected {
            background-color: #e6f7ff;
        }
    }
}

.record-id-cell {
    justify-content: center;
}
</style>
